<script setup lang="ts">
import { computed } from 'vue';
import type { Collection } from '~/utilities/pyscript-api-models';
import CollectionKebabMenu from '~/components/collections/CollectionKebabMenu.vue';
import { useUserStore } from '~/stores/user-store';
import IconLock from '~icons/material-symbols/lock';
import { useCollectionsStore } from '~/stores/collections-store';

const props = defineProps<{
  collection: Collection;
}>();

const userStore = useUserStore();
const collectionsStore = useCollectionsStore();

const isCollectionOwner = computed(() => {
  if (!userStore.isLoggedIn) return false;
  return userStore.user?.id === props.collection.user_id;
});
</script>

<template>
  <div class="group relative block pb-1">
    <div
      class="absolute -bottom-1 top-0 w-full rounded border border-new-gray-100 bg-white dark:border-new-gray-950/80 dark:bg-gray-675"
    />
    <div
      class="absolute -bottom-0 top-0 w-full rounded border border-new-gray-100/90 bg-white dark:border-new-gray-950/80 dark:bg-gray-675"
    />

    <div
      class="relative flex h-full flex-col rounded rounded-br-[32px] border border-new-gray-100/80 bg-white p-5 transition-shadow group-hover:border-new-gray-100 group-hover:shadow-lg dark:border-new-gray-950/80 dark:bg-gray-675 group-hover:dark:border-new-gray-400"
    >
      <router-link
        :to="{
          name: 'collection-projects',
          params: {
            collectionId: collection.id,
            usernameOrUserId: collection.username || collection.user_id,
          },
        }"
        class="mb-2 w-full truncate pr-4 text-sm font-semibold before:absolute before:inset-[0_0_-8px_0] before:block"
      >
        {{ collection.name }}
      </router-link>

      <CollectionKebabMenu
        v-if="isCollectionOwner"
        :collection="collection"
        class="absolute right-2.5 top-4"
      />

      <div class="mt-1 h-[3lh] text-sm text-new-gray-600 dark:text-new-gray-400">
        <button
          v-if="!collection.description && isCollectionOwner"
          class="relative italic hover:underline"
          @click="collectionsStore.openCollectionModal(collection)"
        >
          Add a description...
        </button>
        <p class="line-clamp-3">
          {{ collection.description }}
        </p>
      </div>

      <div
        class="mt-4 flex items-center gap-1 text-xs font-medium text-new-gray-500 dark:text-new-gray-300"
      >
        <IconLock v-if="collection.private" class="inline-block shrink-0 text-new-blue-400" />

        <span>{{ $t('collections.project_count', collection.projects.length) }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss"></style>
